import { z } from 'zod';

/** Coerce to number, treating empty/NaN as null */
const optionalNumber = z.preprocess(
  (v) => (v === '' || v === undefined || v === null || Number.isNaN(Number(v)) ? null : Number(v)),
  z.number().nullable().optional(),
);

// ─── Shared Sub-schemas ─────────────────────────────────────────────────────

const conditionEnum = z.enum(['new', 'used']);
// Phase 50 + #85 follow-up: clean 2-value publishing enum.
const websiteStatusEnum = z.enum(['published', 'disabled']);
const expiryTypeEnum = z.enum(['never', 'days', 'date']);

const optionalString = z.preprocess(
  (v) => (v === null || v === undefined ? undefined : String(v)),
  z.string().optional(),
);

const equipmentItemSchema = z.object({
  item: z.string().min(1, 'Item name required'),
  // The boat resource serializes a null description for equipment rows that have
  // none (the common AI-extracted case). optionalString coerces null → undefined
  // so `.optional()` accepts it — mirrors engineItemSchema's nullable handling.
  description: optionalString,
  sort_order: optionalNumber,
});

const engineItemSchema = z.object({
  engine_manufacturer: optionalString,
  engine_model: optionalString,
  engine_horsepower: optionalNumber,
  engine_year: optionalNumber,
  engine_hours: optionalNumber,
  engine_serial_number: optionalString,
  position: optionalString,
  sort_order: optionalNumber,
});

// ─── Step 1: Boat & Photos ──────────────────────────────────────────────────
// Fields from: basicInfoSchema + featuredImageSchema + galleryImagesSchema

export const boatAndPhotosSchema = z.object({
  condition: conditionEnum,
  manufacturer: z.string().min(1, 'Manufacturer is required'),
  model: z.string().min(1, 'Model is required'),
  model_year: z.coerce
    .number()
    .min(1900, 'Invalid year')
    .max(new Date().getFullYear() + 2, 'Year too far in future'),
  featured_media_id: z.number().nullable().optional(),
  gallery_media_ids: z.array(z.number()).optional(),
});

// ─── Step 2: Specs & Config ─────────────────────────────────────────────────
// Fields from: specificationsSchema + engineConfigSchema + trailerSchema
//              + standardEquipmentSchema + optionalEquipmentSchema

export const specsAndConfigSchema = z.object({
  // Physical specs
  length_ft: optionalNumber,
  beam_in: optionalNumber,
  draft_in: optionalNumber,
  seating_capacity: optionalNumber,
  exterior_color: z.string().optional(),
  interior_color: z.string().optional(),
  hin_number: z.string().max(20, 'HIN must be 20 characters or less').optional(),
  fuel_type: z.string().optional(),
  boat_types: z.array(z.string()).optional(),
  // Engine configuration
  engine_type: z.string().optional(),
  engines: z.array(engineItemSchema).optional(),
  // Trailer
  trailer_vin: z.string().optional(),
  trailer_make: z.string().optional(),
  trailer_model: z.string().optional(),
  trailer_axles: z.string().optional(),
  // Equipment
  standard_equipment: z.array(equipmentItemSchema).optional(),
  optional_equipment: z.array(equipmentItemSchema).optional(),
});

// ─── Step 3: Pricing ────────────────────────────────────────────────────────
// Fields from: pricingSchema (unchanged field set)

export const pricingSchema = z.object({
  our_price: optionalNumber,
  msrp: optionalNumber,
  regular_price: optionalNumber,
  sale_price: optionalNumber,
  cost_price: optionalNumber,
  is_on_sale: z.boolean().optional(),
  boat_cost_price: optionalNumber,
  boat_retail_price: optionalNumber,
  invoice_amount: optionalNumber,
  rigging: optionalNumber,
  freight: optionalNumber,
});

// ─── Step 4: Listing Details ────────────────────────────────────────────────
// Fields from: detailsSchema + summarySchema (location fields moved here)

export const listingDetailsSchema = z.object({
  description: z.string().optional(),
  short_description: z.string().optional(),
  website_status: websiteStatusEnum.optional(),
  status_id: z.string().optional(),
  first_available_date: z.string().optional().nullable(),
  ribbon_id: z.string().optional(),
  ribbon_expiry_type: expiryTypeEnum.optional(),
  ribbon_expiry_value: z.string().optional(),
  location_ids: z.array(z.string()).min(1, 'At least one location is required'),
  primary_location_id: z.string().min(1, 'Primary location is required'),
});

// ─── Step 5: Review & Create ────────────────────────────────────────────────
// Empty schema -- always passes validation

export const reviewSchema = z.object({});

// ─── Step Schemas Array (indexed by step number, 0-based) ───────────────────

export const stepSchemas = [
  boatAndPhotosSchema, // Step 0: Boat & Photos
  specsAndConfigSchema, // Step 1: Specs & Config
  pricingSchema, // Step 2: Pricing
  listingDetailsSchema, // Step 3: Listing Details
  reviewSchema, // Step 4: Review & Create
] as const;

// ─── Full Wizard Schema (for final submit validation) ───────────────────────
// Uses Zod v4 spread syntax instead of deprecated .merge() chains

export const fullWizardSchema = z.object({
  ...boatAndPhotosSchema.shape,
  ...specsAndConfigSchema.shape,
  ...pricingSchema.shape,
  ...listingDetailsSchema.shape,
});
